import { useRef, useState } from 'react';
import { z } from 'zod';
import type { CreatePlanCommand, PlanDto, UpdatePlanCommand } from '@/api/generated';
import { useCreatePlan, useUpdatePlan, useUploadPlan } from '@/api/hooks';
import { Btn } from '@/design-system/primitives/Btn/Btn';
import {
  Dialog,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogTitle,
} from '@/design-system/primitives/Dialog/Dialog';
import { Form, FormField, FormSubmitButton } from '@/design-system/primitives/Form/Form';
import { Input } from '@/design-system/primitives/Input/Input';
import styles from './PlanDialogs.module.css';

// BF-8 — PlanCreateDialog now exposes an optional file picker. If a file is
// attached the dialog hits POST /api/plans/upload (multipart, BF-1 backend);
// otherwise it falls back to the JSON-only POST /api/plans for a
// metadata-only record. PlanEditDialog stays metadata-only (no replace-file
// flow yet — re-upload as a new plan if the PDF changes).

const DISCIPLINE_OPTIONS: Array<{ id: string; label: string }> = [
  { id: 'Arch', label: 'Architectuur' },
  { id: 'Tech', label: 'Technieken' },
  { id: 'Stab', label: 'Stabiliteit' },
];

const schema = z.object({
  name: z.string().trim().min(2, 'Minstens 2 tekens'),
  discipline: z.string().min(1, 'Kies een discipline'),
  version: z.string().trim().min(1, 'Verplicht veld'),
});

type PlanFormValues = z.infer<typeof schema>;

function FieldsBody() {
  return (
    <>
      <FormField<PlanFormValues> name="name" label="Naam" required>
        {(field) => (
          <Input
            {...field}
            value={(field.value as string | undefined) ?? ''}
            autoFocus
            placeholder="Bv. Grondplan verdiep 0"
          />
        )}
      </FormField>
      <FormField<PlanFormValues> name="discipline" label="Discipline" required>
        {(field) => (
          <select
            {...field}
            className={styles.select}
            value={(field.value as string) ?? ''}
            onChange={(e) => field.onChange(e.target.value)}
          >
            {DISCIPLINE_OPTIONS.map((d) => (
              <option key={d.id} value={d.id}>
                {d.label}
              </option>
            ))}
          </select>
        )}
      </FormField>
      <FormField<PlanFormValues> name="version" label="Versie" required>
        {(field) => (
          <Input {...field} value={(field.value as string | undefined) ?? ''} placeholder="v1" />
        )}
      </FormField>
    </>
  );
}

export function PlanCreateDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const createMutation = useCreatePlan();
  const uploadMutation = useUploadPlan();
  const [file, setFile] = useState<File | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleOpenChange = (next: boolean) => {
    if (!next) {
      // Drop the staged file when closing so a reopen starts clean.
      setFile(null);
      if (inputRef.current) inputRef.current.value = '';
    }
    onOpenChange(next);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent>
        <DialogTitle>Nieuw plan</DialogTitle>
        <DialogDescription>
          Voeg een plan-record toe (naam + discipline + versie). Optioneel: voeg de PDF toe — die
          wordt dan downloadbaar vanuit de plannenlijst.
        </DialogDescription>
        <DialogBody>
          <Form<PlanFormValues>
            schema={schema}
            defaultValues={{
              name: '',
              discipline: 'Arch',
              version: 'v1',
            }}
            onSubmit={async (values) => {
              if (file) {
                await uploadMutation.mutateAsync({
                  file,
                  name: values.name.trim(),
                  discipline: values.discipline,
                  version: values.version.trim(),
                });
              } else {
                const body: CreatePlanCommand = {
                  name: values.name.trim(),
                  discipline: values.discipline,
                  version: values.version.trim(),
                  approved: null,
                  active: null,
                };
                await createMutation.mutateAsync(body);
              }
              handleOpenChange(false);
            }}
          >
            <FieldsBody />
            <div className={styles.fileField}>
              <label htmlFor="plan-file-input" className={styles.fileLabel}>
                PDF (optioneel)
              </label>
              <input
                id="plan-file-input"
                ref={inputRef}
                type="file"
                accept="application/pdf,image/*"
                onChange={(e) => setFile(e.target.files?.[0] ?? null)}
              />
              {file && (
                <div className={styles.fileMeta}>
                  {file.name} · {(file.size / 1024).toFixed(0)} KB
                </div>
              )}
            </div>
            <DialogFooter>
              <DialogClose asChild>
                <Btn type="button">Annuleer</Btn>
              </DialogClose>
              <FormSubmitButton>{file ? 'Upload + toevoegen' : 'Toevoegen'}</FormSubmitButton>
            </DialogFooter>
          </Form>
        </DialogBody>
      </DialogContent>
    </Dialog>
  );
}

export function PlanEditDialog({
  open,
  onOpenChange,
  plan,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  plan: PlanDto;
}) {
  const updateMutation = useUpdatePlan(plan.id);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogTitle>Plan bewerken</DialogTitle>
        <DialogDescription>Pas naam, discipline, versie of vlaggen aan.</DialogDescription>
        <DialogBody>
          <Form<PlanFormValues>
            schema={schema}
            defaultValues={{
              name: plan.name,
              discipline: plan.discipline,
              version: plan.version,
            }}
            onSubmit={async (values) => {
              const body: UpdatePlanCommand = {
                name: values.name.trim() || null,
                discipline: values.discipline || null,
                version: values.version.trim() || null,
                approved: null,
                active: null,
              };
              await updateMutation.mutateAsync(body);
              onOpenChange(false);
            }}
          >
            <FieldsBody />
            <DialogFooter>
              <DialogClose asChild>
                <Btn type="button">Annuleer</Btn>
              </DialogClose>
              <FormSubmitButton>Opslaan</FormSubmitButton>
            </DialogFooter>
          </Form>
        </DialogBody>
      </DialogContent>
    </Dialog>
  );
}
